import { Flex, Text, Tooltip } from "@chakra-ui/react";
import { memo, useMemo } from "react";

export interface AssignedItemsCellProps {
  items: ReadonlyArray<string>;
  maxVisible?: number;
  ariaLabel?: string;
}

/**
 * AssignedItemsCell renders a compact list where the first N items are shown as pills
 * and the rest are collapsed into a "+N" pill with a tooltip listing hidden items.
 */
export const AssignedItemsCell = memo(({ items, maxVisible = 1, ariaLabel = "Assigned items" }: AssignedItemsCellProps) => {
  const { visibleItems, hiddenItems } = useMemo(() => {
    const safeItems = Array.isArray(items) ? items.filter(Boolean) : [];
    const visible = safeItems.slice(0, Math.max(0, maxVisible));
    const hidden = safeItems.slice(Math.max(0, maxVisible));
    return { visibleItems: visible, hiddenItems: hidden };
  }, [items, maxVisible]);

  const hasHidden = hiddenItems.length > 0;

  return (
    <Flex alignItems="center" gap="2px" width="100%" overflow="hidden" minHeight="20px" aria-label={ariaLabel}>
      {visibleItems.map((name) => (
        <Text
          key={name}
          fontSize={12}
          fontWeight={600}
          lineHeight="16px"
          padding="2px 8px"
          borderRadius="9999px"
          border="1px solid #e1e7ef"
          whiteSpace="nowrap"
          flexShrink={0}
        >
          {name}
        </Text>
      ))}
      {hasHidden && (
        <Tooltip
          label={
            <Flex flexDirection="column" gap="2px">
              {hiddenItems.map((name) => (
                <Text key={name} fontSize={12}>
                  {name}
                </Text>
              ))}
            </Flex>
          }
          placement="top"
          hasArrow
          bg="gray.700"
          color="white"
          borderRadius="6px"
          p={2}
          aria-label={`Hidden items: ${hiddenItems.join(", ")}`}
        >
          <Text
            fontSize={12}
            fontWeight={600}
            lineHeight="16px"
            padding="2px 10px"
            borderRadius="9999px"
            border="1px solid #e1e7ef"
            bg="#f7fafc"
            color="#4a5568"
            cursor="pointer"
            whiteSpace="nowrap"
            flexShrink={0}
            _hover={{ bg: "#edf2f7", borderColor: "#cbd5e0" }}
          >
            +{hiddenItems.length}
          </Text>
        </Tooltip>
      )}
    </Flex>
  );
});

export default AssignedItemsCell;
